import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { get, getTodos } from '../nucleo/api';
import { useSessaoOpcional } from '../nucleo/sessao';
import { Caixas } from './caixas';
import { Estado } from './estados';
import { Alternador, Rotulo, Selecao } from './formulario';

/**
 * E1 (lote 09/10, item 6, opção A, a parte das listas; achado R37-SPEC-M2): com quem compartilhar uma visão salva e o
 * "Mostrar em Relatórios", no diálogo de salvar e no de editar a visão da `Tabela`. Os mesmos componentes e o mesmo modelo
 * do relatório salvo (`modules/relatorios/salvos.tsx`): `Selecao` do compartilhamento, caixas de papéis ou pessoas e
 * `Alternador`. Compartilhar aparece para quem tem `relatorios.compartilhar`; o "Mostrar em Relatórios", com o Relatórios
 * instalado e `relatorios.ver`. Quem abre a visão roda a lista com as próprias permissões e empresa (o servidor confere).
 */

export const COMPARTILHAMENTOS_VISAO = ['privado', 'papeis', 'pessoas', 'workspace'] as const;
export type CompartilhamentoVisao = (typeof COMPARTILHAMENTOS_VISAO)[number];

export interface ValorCompartilhar {
  compartilhamento: CompartilhamentoVisao;
  papeis: string[];
  pessoas: string[];
  em_relatorios: boolean;
}

export const SEM_COMPARTILHAR: ValorCompartilhar = { compartilhamento: 'privado', papeis: [], pessoas: [], em_relatorios: false };

interface Item {
  id: string;
  nome: string;
}

/** O que a pessoa pode mudar aqui (sem sessão, como nos testes da `Tabela`, nada aparece). */
export function usePodeCompartilharVisao() {
  const s = useSessaoOpcional();
  return {
    compartilhar: !!s?.pode('relatorios.compartilhar'),
    relatorios: !!s && s.instalado('relatorios') && s.pode('relatorios.ver'),
    usuarioId: s?.usuario.id ?? null,
  };
}

/** Os campos de compartilhar e de "Mostrar em Relatórios" (o pai guarda o valor e manda à API só o que aparece). */
export function CamposCompartilharVisao({ valor, aoMudar }: { valor: ValorCompartilhar; aoMudar: (v: ValorCompartilhar) => void }) {
  const { t } = useTranslation();
  const pode = usePodeCompartilharVisao();
  const c = valor.compartilhamento;
  const papeis = useQuery({ queryKey: ['/relatorios/papeis'], queryFn: () => get<{ itens: Item[] }>('/relatorios/papeis'), enabled: pode.compartilhar && c === 'papeis' });
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<Item>('/membros'), enabled: pode.compartilhar && c === 'pessoas', staleTime: 60_000 });
  if (!pode.compartilhar && !pode.relatorios) return null;
  return (
    <>
      {pode.compartilhar && (
        <Rotulo id="visao-compartilhar" rotulo={t('tabela.compartilhar')} ajuda={t('tabela.compartilharAjuda')}>
          <Selecao
            id="visao-compartilhar"
            value={c}
            onChange={(e) => aoMudar({ ...valor, compartilhamento: e.target.value as CompartilhamentoVisao })}
            opcoes={COMPARTILHAMENTOS_VISAO.map((x) => ({ valor: x, rotulo: t(`tabela.compartilhamentos.${x}`) }))}
          />
        </Rotulo>
      )}
      {pode.compartilhar && c === 'papeis' && (
        <Estado consulta={papeis}>{(d) => <Caixas id="visao-papeis" rotulo={t('tabela.compartilhamentos.papeis')} itens={d.itens} valor={valor.papeis} aoMudar={(papeis) => aoMudar({ ...valor, papeis })} />}</Estado>
      )}
      {pode.compartilhar && c === 'pessoas' && (
        <Estado consulta={membros}>
          {(d) => <Caixas id="visao-pessoas" rotulo={t('tabela.compartilhamentos.pessoas')} pessoas itens={d.itens.filter((x) => x.id !== pode.usuarioId)} valor={valor.pessoas} aoMudar={(pessoas) => aoMudar({ ...valor, pessoas })} />}
        </Estado>
      )}
      {pode.relatorios && (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-tinta-1">{t('tabela.emRelatorios')}</p>
            <p className="text-xs text-tinta-3">{t('tabela.emRelatoriosAjuda')}</p>
          </div>
          <Alternador id="visao-em-relatorios" rotulo={t('tabela.emRelatorios')} marcado={valor.em_relatorios} aoMudar={(em_relatorios) => aoMudar({ ...valor, em_relatorios })} />
        </div>
      )}
    </>
  );
}

/** O corpo a mandar à API: só os campos que a pessoa vê (o resto o servidor mantém ou usa o padrão). */
export function corpoCompartilhar(valor: ValorCompartilhar, pode: { compartilhar: boolean; relatorios: boolean }): Partial<ValorCompartilhar> {
  return {
    ...(pode.compartilhar ? { compartilhamento: valor.compartilhamento, papeis: valor.papeis, pessoas: valor.pessoas } : {}),
    ...(pode.relatorios ? { em_relatorios: valor.em_relatorios } : {}),
  };
}

/**
 * Link de uma visão salva: a lista da tela com `?visao_salva=<id>`; a `Tabela` da tela aplica a visão ao abrir (se ela a
 * enxerga) e tira o parâmetro da URL. `?visao=` já é o modo do Plano de contas (B5), por isso outro nome.
 */
export const PARAMETRO_VISAO_SALVA = 'visao_salva';
/**
 * Review r38 spec (B3): a lista que mora numa sub-aba de estado local (Treinamento > Progresso, "Por pessoas" ou "Por
 * cursos") precisa saber qual abrir: o link leva também a tela da visão, e a página escolhe a sub-aba por ela. A `Tabela`
 * tira os dois parâmetros da URL ao aplicar.
 */
export const PARAMETRO_TELA_DA_VISAO = 'visao_tela';
export const linkDaVisao = (caminhoDaLista: string, id: string, tela?: string) =>
  `${caminhoDaLista}?${PARAMETRO_VISAO_SALVA}=${encodeURIComponent(id)}${tela ? `&${PARAMETRO_TELA_DA_VISAO}=${encodeURIComponent(tela)}` : ''}`;
